import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { 
  CheckCircle2, 
  AlertCircle, 
  Info, 
  X, 
  AlertTriangle, 
  Clock, 
  Trash2,
  Check
} from 'lucide-react';

const toastConfigs = {
  success: {
    icon: CheckCircle2,
    badgeBg: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    iconWrapper: 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30',
    barBg: 'bg-emerald-500',
    glowColor: 'shadow-emerald-950/40',
  },
  cancel: {
    icon: Trash2,
    badgeBg: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    iconWrapper: 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30',
    barBg: 'bg-amber-500',
    glowColor: 'shadow-amber-950/40',
  },
  error: {
    icon: AlertCircle,
    badgeBg: 'bg-rose-500/10 text-rose-300 border-rose-500/20',
    iconWrapper: 'bg-rose-500/15 text-rose-400 ring-1 ring-rose-500/30',
    barBg: 'bg-rose-500',
    glowColor: 'shadow-rose-950/40',
  },
  warning: {
    icon: AlertTriangle,
    badgeBg: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    iconWrapper: 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30',
    barBg: 'bg-amber-500',
    glowColor: 'shadow-amber-950/40',
  },
  info: {
    icon: Info,
    badgeBg: 'bg-sky-500/10 text-sky-300 border-sky-500/20',
    iconWrapper: 'bg-sky-500/15 text-sky-400 ring-1 ring-sky-500/30',
    barBg: 'bg-sky-500',
    glowColor: 'shadow-sky-950/40',
  },
};

// Item Toast đơn lẻ hỗ trợ thanh đếm ngược thời gian
function ToastItem({ toast, onRemove }) {
  const config = toastConfigs[toast.type] || toastConfigs.success;
  const Icon = toast.icon || config.icon;
  const duration = toast.duration || 4500;
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 25);

    return () => clearInterval(interval);
  }, [duration]);

  return (
    <div
      role="alert"
      className={`pointer-events-auto relative overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-900/95 backdrop-blur-xl p-4 text-white shadow-2xl ${config.glowColor} transition-all duration-300 hover:scale-[1.01] hover:border-slate-600 w-full sm:max-w-md animate-in slide-in-from-bottom-4 fade-in`}
    >
      <div className="flex items-start gap-3.5">
        {/* Biểu tượng phát sáng tinh tế */}
        <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${config.iconWrapper} transition-transform`}>
          <Icon className="h-5 w-5" />
        </div>

        {/* Khối nội dung thông báo */}
        <div className="flex-1 min-w-0 pr-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h4 className="text-xs font-bold tracking-wide text-slate-100">
              {toast.title || toast.message}
            </h4>
            {toast.badge && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${config.badgeBg}`}>
                <Clock className="w-2.5 h-2.5" />
                {toast.badge}
              </span>
            )}
          </div>

          {/* Dòng mô tả chi tiết nếu có */}
          {(toast.description || (toast.title && toast.message)) && (
            <p className="text-[11px] leading-relaxed text-slate-300/90 font-normal">
              {toast.description || toast.message}
            </p>
          )}

          {/* Ghi chú thêm (vd: thông tin tự động xóa sau 12h) */}
          {toast.extraNote && (
            <div className="mt-2 flex items-center gap-1.5 text-[10px] text-amber-300/90 bg-amber-500/10 px-2 py-1 rounded-lg border border-amber-500/20">
              <Clock className="w-3 h-3 shrink-0" />
              <span>{toast.extraNote}</span>
            </div>
          )}

          {/* Nút hành động bổ sung (nếu truyền) */}
          {toast.action && (
            <div className="mt-2.5">
              <button
                type="button"
                onClick={() => {
                  toast.action.onClick?.();
                  onRemove(toast.id);
                }}
                className="text-[11px] font-bold text-emerald-400 hover:text-emerald-300 underline underline-offset-2 transition"
              >
                {toast.action.label}
              </button>
            </div>
          )}
        </div>

        {/* Nút đóng Toast */}
        <button
          onClick={() => onRemove(toast.id)}
          className="shrink-0 rounded-lg p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition"
          aria-label="Đóng"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Thanh tiến trình tự động biến mất */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-800/80">
        <div
          className={`h-full transition-all ease-linear ${config.barBg}`}
          style={{ width: `${progress}%`, transitionDuration: '25ms' }}
        />
      </div>
    </div>
  );
}

const ToastContext = createContext();

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /**
   * showToast hỗ trợ 2 cú pháp:
   * 1. Cú pháp truyền thống: showToast(message, type, duration)
   * 2. Cú pháp nâng cao: showToast({ title, message, description, type, duration, badge, extraNote, action })
   */
  const showToast = useCallback((param1, param2 = 'success', param3 = 4500, extraOptions = {}) => {
    const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    let toastObj = {};
    if (typeof param1 === 'object' && param1 !== null) {
      toastObj = {
        id,
        duration: param1.duration || 4500,
        type: param1.type || 'success',
        ...param1,
      };
    } else {
      toastObj = {
        id,
        message: param1,
        type: param2,
        duration: param3,
        ...extraOptions,
      };
    }

    setToasts((prev) => [...prev, toastObj]);

    const timer = setTimeout(() => {
      removeToast(id);
    }, toastObj.duration || 4500);

    return () => clearTimeout(timer);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      
      {/* Container hiển thị danh sách Toast ở góc dưới cùng bên phải */}
      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col space-y-3 pointer-events-none w-[90vw] max-w-md">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast phải được sử dụng bên trong ToastProvider');
  }
  return context;
};
export default ToastContext;